import moment from 'moment';
import {getMessage} from 'epic-diesel-common';
import {getLocale} from 'epic-common/src/locale';
import {isNumber, isPlainObject} from './TypeUtils';
import {FIND_DOT_MIDDLE_STR_REGEX} from '../constants/regex';

const VALID_STRING_VALUES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
export const generateRandomString = length => {
    let retVal = '';

    for (let i = 0; i < length; i++) {
        retVal += VALID_STRING_VALUES.charAt(
            Math.floor(Math.random() * VALID_STRING_VALUES.length)
        );
    }

    return retVal;
};

/**
 * Formats date by locale using moment.js
 * @param {string | number} timestamp - required
 * @param {string} locale - optional
 * @return {string} formatted date - see https://momentjs.com/
 */
export const formatDateDiffByLocale = (timestamp, locale = null) => {
    const currentDate = moment();
    const givenDate = moment(timestamp);

    // format date based on range between now and then
    const dayDiff = givenDate.diff(currentDate, 'days');

    try {
        const _locale = locale || getLocale();
        if (_locale) {
            givenDate.locale(_locale);
        }
    } catch (err) {
        // do nothing
    }

    if (dayDiff <= 1) {
        // Return relative date
        return givenDate.fromNow();
    } else if (dayDiff < 10) {
        // return calendar date
        return givenDate.calendar();
    } else {
        // return date
        return givenDate.format('ll');
    }
};

/**
 * Format timestamp by locale
 * @param timestamp {string} required
 * @param locale {string} optional
 * @param config {object}
 * @doc https://momentjs.com/
 */
export const formatDateByLocale = (
    timestamp,
    locale?,
    format?,
    callback?: (moment: moment.Moment) => moment.Moment
) => {
    if (!timestamp) return null;
    let givenDate = moment(timestamp);
    try {
        const _locale = locale || getLocale();
        if (_locale) {
            givenDate.locale(_locale);
        }

        if (typeof callback === 'function') {
            givenDate = callback(givenDate);
        }
    } catch (err) {
        // do nothing
    }
    return givenDate.format(format || 'll');
};

export function safeJSONStringify(json, pretty?: boolean) {
    let result = 'Unable to process JSON';
    try {
        result = pretty ? JSON.stringify(json, null, 2) : JSON.stringify(json);
    } catch (e) {
        console.error('Failed to process json, ', e.message);
    }
    return result;
}

export const getDaysInMonth = (month: number): number => {
    return new Date(1970, month, 0).getDate();
};

// transform cms messages etc
export const transformMessage = function getMessage(message: string, args) {
    let newMessage = message;
    if (args && newMessage) {
        for (const i in args) {
            if (Object.prototype.hasOwnProperty.call(args, i)) {
                const find = `\\{${i}\\}`;
                const re = new RegExp(find, 'g');
                newMessage = newMessage.replace(re, args[i]);
            }
        }
    }
    return newMessage;
};

export const formatCurrency = (
    num: string | number,
    locale = 'en-US',
    currencyType = 'USD',
    decimal?
): string => {
    let amount = num || 0;

    if (decimal) {
        amount = (Number(amount) / 10 ** decimal).toFixed(decimal);
    }

    try {
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyType
        });
        return formatter.format(Number(amount));
    } catch (err) {
        try {
            return amount.toLocaleString('en-US', {style: 'currency', currency: currencyType}); // Legacy
        } catch (err) {
            return String(amount);
        }
    }
};

export const formatAmount = (
    type: 'number' | 'currency' | 'percent' | 'text',
    amount: string | number = 0
) => {
    switch (type) {
        case 'number': {
            return amount.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            });
        }
        case 'currency': {
            const newContent = formatCurrency(amount);
            const value = newContent.match(/[\w+\\,*]+\./)[0];
            const symbols = newContent.split(/[\w+\\,*]+\./);
            const location = newContent.startsWith(value);
            if (location) {
                return [value, symbols[0], symbols[1]];
            }
            return [symbols[0], value, symbols[1]];
        }
        case 'percent': {
            return `${amount}%`;
        }
        case 'text':
        default: {
            return amount;
        }
    }
};

export const getNestedObjKV = <T>(nestedObj: T, keyPath: string[]) => {
    if (keyPath.length === 1) {
        return nestedObj && nestedObj[keyPath[0]];
    }

    return keyPath.reduce(
        (obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined),
        nestedObj
    );
};

export const sortArrayByKey = <T>(
    items: T[] = [],
    keyPath: string,
    sortAsc = true,
    delimiter = '.'
) => {
    const keys = keyPath.split(delimiter);
    const isNumber = !isNaN(getNestedObjKV(items[0], keys));

    if (isNumber) {
        return items.sort((a, b) => {
            const valA = getNestedObjKV(a, keys);
            const valB = getNestedObjKV(b, keys);
            return isNaN(valA) || !sortAsc ? valA - valB : valB - valA;
        });
    }

    return items.sort((a, b) => {
        const valA = getNestedObjKV(a, keys);
        const valB = getNestedObjKV(b, keys);

        if (valA === valB) {
            return (sortAsc ? -1 : 1) * (valB < valA ? -1 : 1);
        }

        if (valA < valB) {
            return sortAsc ? -1 : 1;
        }

        if (valA > valB) {
            return sortAsc ? 1 : -1;
        }
        return 0;
    });
};

export const uniqifyByKey = <T>(arr1: T[], arr2: T[], key: string): T[] => {
    const hash = new Map<string, T>();
    for (const entry of arr1.concat(arr2)) {
        if (entry[key]) {
            hash.set(entry[key], Object.assign(hash.get(entry[key]) || {}, entry));
        }
    }
    return Array.from(hash, ([key, entry]) => entry);
};

enum TimeInSeconds {
    SECOND = 1,
    MINUTE = 60,
    HOUR = MINUTE * 60,
    DAY = HOUR * 24,
    MONTH = DAY * 30,
    YEAR = DAY * 365
}

const handlePluralTranslation = (
    messages: Record<string, string>,
    singularCode: string,
    pluralCode: string,
    value: number
) => {
    return getMessage(messages, value === 1 ? singularCode : pluralCode, [value]);
};

export const formatDuration = (messages: Record<string, string>, totalSeconds: number) => {
    if (!totalSeconds) return undefined;
    let time: number;

    const seconds = Math.round(totalSeconds);
    const timePeriod =
        (time = seconds / TimeInSeconds.YEAR) >= 1
            ? 'year'
            : (time = seconds / TimeInSeconds.MONTH) >= 1
            ? 'month'
            : (time = seconds / TimeInSeconds.DAY) >= 1
            ? 'day'
            : (time = seconds / TimeInSeconds.HOUR) >= 1
            ? 'hour'
            : (time = seconds / TimeInSeconds.MINUTE) >= 1
            ? 'minute'
            : 'second';

    return handlePluralTranslation(
        messages,
        `epic.affiliate.common.time.${timePeriod}`,
        `epic.affiliate.common.time.${timePeriod}s`,
        timePeriod === 'second' ? seconds : Math.round(time)
    );
};

export const formatRelativeTime = (
    messages: Record<string, string>,
    isoTimestamp: string,
    timeLeft?: boolean
) => {
    const diffMilli = new Date(isoTimestamp).valueOf() - new Date().valueOf();
    const diffSeconds = Math.floor(diffMilli / 1000);
    const duration = formatDuration(messages, diffSeconds);

    if (!duration) {
        return getMessage(messages, 'epic.affiliate.common.time.just_now');
    }

    return getMessage(
        messages,
        `epic.affiliate.common.time.${timeLeft ? 'time_left' : 'time_past'}`,
        [duration]
    );
};

export const listToMatrix = <T extends any[]>(arr: T, width: number = 2): T[] => {
    if (!arr || !(arr instanceof Array)) return arr;
    return arr.reduce((prev, entry, index) => {
        if (index % width == 0) {
            prev.push([entry]);
        } else {
            prev[prev.length - 1].push(entry);
        }
        return prev;
    }, []);
};

/**
 * Flattens an object from `{a: {b: {c: 'd'}}}` to `{'a.b.c': 'd'}`
 */
export const flattenObject = <T extends {} | []>(
    object: T,
    result?: object,
    prefix?: string,
    untilDepth?: number
): Record<string, any> => {
    if (!object) return object;
    const _result = result || {};

    if (!isPlainObject(object)) {
        if (!prefix) return object;
        _result[prefix] = object;
        return _result;
    }

    if (isNumber(untilDepth)) {
        untilDepth--;
    }

    const formattedPrefix = prefix ? `${prefix}.` : '';
    return Object.keys(object).reduce((carry, key) => {
        const flattenedKey = formattedPrefix + key;
        // last iteration or not
        const extra =
            untilDepth === -1
                ? {[flattenedKey]: object[key]}
                : flattenObject(object[key], _result, flattenedKey, untilDepth);
        return Object.assign(carry, extra);
    }, {});
};

/**
 * Reverse of flattenObject
 * Unflattens an object from `{'a.b.c': 'd'}` to `{a: {b: {c: 'd'}}}`
 */
export const unflattenObject = <T extends {} | []>(ob: T): Record<string, any> => {
    if (!isPlainObject(ob)) return ob;
    const result = {};
    for (const i in ob) {
        if (Object.prototype.hasOwnProperty.call(ob, i)) {
            const keys = i.match(FIND_DOT_MIDDLE_STR_REGEX);
            keys.reduce((prev, key, index) => {
                if (prev[key]) return prev[key];
                return (prev[key] = isNaN(Number(keys[index + 1]))
                    ? keys.length - 1 === index
                        ? ob[i]
                        : {}
                    : []);
            }, result);
        }
    }
    return result;
};
